import { type OptionItem } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { sort } from "@common/lib/array";
import date from "@common/lib/date";
import { formatDisplayValue } from "@common/lib/format";
import getOptions from "@common/lib/options";
import { getApi } from "@common/modules/data/api";
import { type Data } from "@common/modules/data/types";
import { getApi as getUserApi } from "@common/modules/user/api";
import { getUserDisplayName } from "@common/modules/user/lib";
import { groupBy } from "lodash";
import React from "react";
import { type ChartTypeItem, type GridConfigColumn } from "../../types";
import { getDateType, getTotalSaleExtraColumns } from "../lib";
import { type ProviderProps, type TotalSaleBase, type TotalSalesBaseRequest } from "../types";
import CommonStaticProvider from "./CommonStaticProvider";

export type TotalSalesByDate = TotalSaleBase & {
  date: string;
};

export type TotalSalesByDateRequest = TotalSalesBaseRequest & {
  accessCardId: Date;
  contactUserId: Date;
  countryCode: Date;
  eventId: Date;
  funnelId: Date;
  listId: Date;
  tagId: Date;
};
export type Props = Omit<ProviderProps, "id">;
export const SHOP_TOTAL_SALES_BY_DATE = "815ad80b-40c2-46fd-899f-e92ad87d5946";
const chartTypes: ChartTypeItem<any>[] = [{ type: "Line" }, { type: "SteppedLine" }];

const TotalSalesByDateProvider = (props: Props) => {
  const i18n = useI18n();

  const api = React.useMemo(() => getApi(i18n), [i18n]);
  const options = React.useMemo(() => getOptions(i18n), [i18n]);
  const userApi = React.useMemo(() => getUserApi(i18n), [i18n]);

  const fields = React.useMemo(
    (): Data.Api.FormField[] => [
      {
        formControlKey: "DateRange",
        formFieldId: "dateRange",
        formFieldKey: "Date",
        label: i18n.dateRange,
        plainTextValue: `{"from": "${date.extractDate(date.addMonth(new Date(), -1))}","to": "${date.extractDate(
          new Date()
        )}"}`
      },
      {
        formControlKey: "Select",
        formFieldId: "filter",
        formFieldKey: "Filter",
        isRequired: true,
        label: i18n.filter,
        plainTextValue: "None",
        source: `json:[{"caption":"${i18n.none}" ,"value":"None"},{"caption":"${i18n.accessCard}","value":"AccessCard"},{"caption":"${i18n.country}","value":"Country"},{"caption":"${i18n.event}","value":"Event"},{"caption":"${i18n.user}","value":"User"},{"caption":"${i18n.funnel}","value":"Funnel"},{"caption":"${i18n.list}","value":"List"},{"caption":"${i18n.tag}","value":"Tag"}]`
      },
      {
        formControlKey: "Select",
        formFieldId: "accessCard",
        formFieldKey: "AccessCardId",
        isRequired: true,
        label: i18n.accessCard,
        source: "api:user|accessCards",
        selectBehaviour: "preselect",
        visibilityCondition: "f.Filter == 'AccessCard'"
      },
      {
        formControlKey: "Autocomplete",
        formFieldId: "contactUser",
        formFieldKey: "ContactUserId",
        isRequired: true,
        label: i18n.user,
        selectBehaviour: "preselect",
        source: "api:user|users|page=&size=",
        visibilityCondition: "f.Filter == 'User'"
      },
      {
        formControlKey: "Select",
        formFieldId: "country",
        formFieldKey: "CountryCode",
        isRequired: true,
        label: i18n.country,
        source: "api:system|countries",
        selectBehaviour: "preselect",
        visibilityCondition: "f.Filter == 'Country'"
      },
      {
        formControlKey: "Select",
        formFieldId: "event",
        formFieldKey: "EventId",
        isRequired: true,
        label: i18n.event,
        source: "api:expo|events",
        selectBehaviour: "preselect",
        visibilityCondition: "f.Filter == 'Event'"
      },
      {
        formControlKey: "Select",
        formFieldId: "funnel",
        formFieldKey: "FunnelId",
        isRequired: true,
        label: i18n.funnel,
        source: "api:web|funnels",
        selectBehaviour: "preselect",
        visibilityCondition: "f.Filter == 'Funnel'"
      },
      {
        formControlKey: "Select",
        formFieldId: "list",
        formFieldKey: "ListId",
        isRequired: true,
        label: i18n.list,
        source: "api:user|lists",
        selectBehaviour: "preselect",
        visibilityCondition: "f.Filter == 'List'"
      },
      {
        formControlKey: "Select",
        formFieldId: "tag",
        formFieldKey: "TagId",
        isRequired: true,
        label: i18n.tag,
        source: "api:user|tags",
        selectBehaviour: "preselect",
        visibilityCondition: "f.Filter == 'Tag'"
      }
    ],
    [i18n]
  );

  const getChartGridData = React.useCallback(
    async (id, data) => {
      const res = await api.getTotalSalesByDate(data as any);
      const to = data.StartDate;
      const from = data.EndDate;
      let finalRes = [...sort(res, { key: "date", type: "date" })];

      const dateType = getDateType(from, to, res.length);
      if (dateType === "day" || dateType === "dayName" || dateType === "date" || dateType === "monthDay") {
        finalRes = finalRes.map((x) => ({ ...x, date: formatDisplayValue(x.date, "dateLocal") }));
      } else {
        const groupped = groupBy(finalRes, (x) => formatDisplayValue(x.date, "dateLocal"));
        finalRes = Object.keys(groupped).map((x) => ({
          date: x,
          totalSales: groupped[x].reduce((res, y) => res + y.totalSales, 0),
          orderCount: groupped[x].reduce((res, y) => res + y.orderCount, 0),
          salesAmount: groupped[x].reduce((res, y) => res + y.salesAmount, 0),
          salesWithTaxAmount: groupped[x].reduce((res, y) => res + y.salesWithTaxAmount, 0),
          salesTaxAmount: groupped[x].reduce((res, y) => res + y.salesTaxAmount, 0),
          shippingAmount: groupped[x].reduce((res, y) => res + y.shippingAmount, 0),
          shippingWithTaxAmount: groupped[x].reduce((res, y) => res + y.shippingWithTaxAmount, 0),
          shippingTaxAmount: groupped[x].reduce((res, y) => res + y.shippingTaxAmount, 0),
          surchargeAmount: groupped[x].reduce((res, y) => res + y.surchargeAmount, 0),
          surchargeWithTaxAmount: groupped[x].reduce((res, y) => res + y.surchargeWithTaxAmount, 0),
          surchargeTaxAmount: groupped[x].reduce((res, y) => res + y.surchargeTaxAmount, 0),
          netSales: groupped[x].reduce((res, y) => res + y.netSales, 0),
          netShipping: groupped[x].reduce((res, y) => res + y.netShipping, 0),
          netSurcharges: groupped[x].reduce((res, y) => res + y.netSurcharges, 0),
          netTax: groupped[x].reduce((res, y) => res + y.netTax, 0),
          salesDiscountAmount: groupped[x].reduce((res, y) => res + y.salesDiscountAmount, 0),
          shippingDiscountAmount: groupped[x].reduce((res, y) => res + y.shippingDiscountAmount, 0),
          surchargeDiscountAmount: groupped[x].reduce((res, y) => res + y.surchargeDiscountAmount, 0)
        }));
      }

      return Promise.resolve(finalRes);
    },
    [api]
  );

  const getColumns = React.useCallback((): GridConfigColumn<TotalSalesByDate>[] => {
    return [
      {
        dataType: "string",
        key: "date",
        label: i18n.date
      }
    ];
  }, []);

  const extraColumns = React.useMemo(() => getTotalSaleExtraColumns(i18n), [i18n]);

  return (
    <CommonStaticProvider
      badges={["AccessCardId", "ContactUserId", "EventId", "FunnelId", "CountryCode", "ListId", "StartDate", "TagId"]}
      category="Shop"
      fields={fields}
      isLoading={false}
      onLoaded={props.onLoaded}
    >
      {({ getBadges, getFilters, parameters }) =>
        props.children({
          id: SHOP_TOTAL_SALES_BY_DATE,
          chartTypes,
          config: {
            aspectRatio: 2.5,
            colors: [],
            legend: "hide",
            xAxis: {
              key: "date"
            },
            yAxis: [{ key: "totalSales", color: "{$Blue}", label: i18n.sales }],
            totalRecords: 10000,
            yNoData: "hide"
          },
          extraColumns,
          fields,
          hasGrid: true,
          isLoading: false,
          name: i18n.totalSalesByDate,
          parameters,
          type: "Line",
          getBadges,
          getChartData: getChartGridData,
          getFilters: async (id, values) => {
            let filterOptions: OptionItem<any>[] = [];
            switch (values[0]?.formFieldId) {
              case "contactUser":
                const user = await userApi.getSingle(+values[0].value!);
                filterOptions = [{ label: getUserDisplayName(user), value: user?.userId.toString() }];
                break;
              case "accessCard":
                const accessCardOptions = await options.getOptions("user", "accessCards");
                filterOptions = accessCardOptions;
                break;
              case "country":
                const countryOptions = await options.countries();
                filterOptions = countryOptions;
                break;
              case "event":
                const eventOptions = await options.getOptions("expo", "events");
                filterOptions = eventOptions;
                break;
              case "funnel":
                const funnelOptions = await options.getOptions("web", "funnels");
                filterOptions = funnelOptions;
                break;
              case "list":
                const listOptions = await options.getOptions("user", "lists");
                filterOptions = listOptions;
                break;
              case "contactUser":
                const tagOptions = await options.getOptions("user", "tags");
                filterOptions = tagOptions;
                break;
              default:
                break;
            }

            return getFilters(id, values, filterOptions);
          },
          gridConfig: { columns: [], pageSize: 10000, key: "date" },
          gridColumns: getColumns
        })
      }
    </CommonStaticProvider>
  );
};

export default TotalSalesByDateProvider;
